/**
 * A stand-in for Paystack's hosted page, for test mode only.
 *
 * `BILLING_PROVIDER=test` points its authorization URL here, so the entire
 * flow — choose a plan, leave for a payment page, come back, get verified,
 * get activated — can be walked in a browser with no merchant account and no
 * money. The moment real keys are configured this page is never reached,
 * because Paystack's own URL is returned instead.
 *
 * It deliberately looks nothing like Paystack. Somebody will eventually see
 * this screen on a real deployment by mistake, and it should be immediately
 * obvious that no money is involved.
 */

import { useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { api } from '../../lib/api';
import { Button, Card } from '../../components/ui';

export function TestCheckoutPage(): ReactNode {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { business } = useAuth();
  const [busy, setBusy] = useState(false);

  const reference = params.get('reference') ?? '';

  /**
   * "Pay" means: tell the server's test provider that money arrived, then go
   * back through the ordinary callback. The activation itself still runs the
   * real path — verify, amount check, entitlement move — so what is being
   * exercised is the production flow with one fake at the outermost edge.
   */
  const pay = async (outcome: 'success' | 'failure'): Promise<void> => {
    setBusy(true);
    try {
      await api.post(`/businesses/${business?.businessId}/subscription/test-pay`, {
        reference,
        outcome,
      });
    } finally {
      navigate(`/billing?reference=${encodeURIComponent(reference)}`, { replace: true });
    }
  };

  return (
    <div className="mx-auto max-w-lg px-4 py-16">
      <Card>
        <div className="space-y-4 p-6">
          <div className="rounded-lg border-2 border-dashed border-amber-400 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900">
            Test mode — no real payment is taken. This page stands in for
            Paystack while CenoX has no live keys.
          </div>

          <div>
            <h1 className="text-lg font-semibold text-slate-900">Simulated checkout</h1>
            <p className="mt-1 break-all font-mono text-xs text-slate-500">{reference}</p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button disabled={busy || !reference} onClick={() => void pay('success')}>
              {busy ? 'Working…' : 'Simulate successful payment'}
            </Button>
            <Button
              variant="secondary"
              disabled={busy || !reference}
              onClick={() => void pay('failure')}
            >
              Simulate failure
            </Button>
            <Button variant="ghost" onClick={() => navigate('/billing')}>
              Abandon
            </Button>
          </div>

          <p className="text-xs text-slate-500">
            Abandoning is worth trying: it is the case a real customer hits most
            often, and your plan must be exactly as it was.
          </p>
        </div>
      </Card>
    </div>
  );
}
